import { apiClient } from './api-client';
import { z } from 'zod';
import { HangoutSchema, type Hangout } from '../schemas/hangout.schema';

export const AdminUserSchema = z.object({
    id: z.number().int().positive(),
    handle: z.string(),
    email: z.string().nullable(),
    isAdmin: z.boolean(),
    hasPin: z.boolean(),
    locked: z.boolean().nullable(),
    createdAt: z.coerce.date(),
});
export type AdminUser = z.infer<typeof AdminUserSchema>;
const AdminUsersSchema = z.array(AdminUserSchema);

export const OverviewSchema = z.object({
    users: z.number().int(),
    hangouts: z.number().int(),
    markets: z.number().int(),
    current: HangoutSchema.nullable(),
});
export type Overview = z.infer<typeof OverviewSchema>;

export const AuditRowSchema = z.object({
    id: z.number().int().positive(),
    actorId: z.number().int().positive(),
    actorHandle: z.string(),
    kind: z.string(),
    target: z.string(),
    reason: z.string(),
    createdAt: z.coerce.date(),
});
const AuditSchema = z.array(AuditRowSchema);
export type AuditRow = z.infer<typeof AuditRowSchema>;

export const adminService = {
    overview: (): Promise<Overview> => apiClient.get('admin/overview', OverviewSchema),

    users: (): Promise<AdminUser[]> => apiClient.get('admin/users', AdminUsersSchema),

    createUser: (input: { handle: string; pin: string; isAdmin?: boolean }) =>
        apiClient.post('admin/users', input),

    updateUser: (
        id: number,
        patch: { handle?: string; isAdmin?: boolean; pin?: string; unlock?: boolean },
    ) => apiClient.patch(`admin/users/${id}`, patch),

    createHangout: (input: {
        name: string;
        venue?: string;
        startingStake?: number;
        maxStakePerMarket?: number;
    }): Promise<Hangout> =>
        apiClient.post('admin/hangouts', input, HangoutSchema),


    addPlayer: (hangoutId: number, userId: number): Promise<void> =>
        apiClient.post(`admin/hangouts/${hangoutId}/players/${userId}`),

    closeHangout: (id: number) => apiClient.post(`admin/hangouts/${id}/close`),

    voidMarket: (id: number, reason: string) =>
        apiClient.post(`admin/markets/${id}/void`, { reason }),

    adjust: (input: { hangoutId: number; userId: number; delta: number; reason: string }) =>
        apiClient.post('admin/ledger/adjust', input),

    audit: (): Promise<AuditRow[]> => apiClient.get('admin/audit', AuditSchema),
};
